import { useState } from 'react';
import { useAppDispatch } from '@/stores/hooks';
import { uploadVaultThunk } from '@/stores/thunks/upload-vault-thunk';
import { deleteVaultThunk } from '@/stores/thunks/delete-vault-thunk';
import { clearVaultDeleted } from '@/stores/vault-slice';
import { useSyncMachineService } from '@/contexts/sync-machine-context';
import type { VaultDeletedInfo } from '@/stores/state';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { PrimaryButton } from './ui/primary-button';
import { SecondaryButton } from './ui/secondary-button';
import { AlertIcon } from './icons/alert-icon';

interface VaultDeletedDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  deletedInfo: VaultDeletedInfo | null;
}

export function VaultDeletedDialog({
  open,
  onOpenChange,
  deletedInfo,
}: VaultDeletedDialogProps) {
  const dispatch = useAppDispatch();
  const syncMachineService = useSyncMachineService();
  const [processing, setProcessing] = useState(false);

  if (!deletedInfo) return null;

  const handleDeleteLocal = async () => {
    setProcessing(true);
    try {
      // Delete the vault locally
      await dispatch(deleteVaultThunk({ vaultId: deletedInfo.vaultId })).unwrap();
      
      // Reset sync state
      dispatch(clearVaultDeleted());
      
      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('[Vault Deleted Dialog] Failed to delete local vault:', error);
      alert('Failed to delete local vault. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handleReupload = async () => {
    setProcessing(true);
    try {
      // Transition machine: LocalOnly → Registering
      const machine = syncMachineService.get(deletedInfo.vaultId);
      if (machine && machine.status === 'local-only') {
        machine.register();
      }

      // Re-upload without conditional headers (force upload)
      const result = await dispatch(uploadVaultThunk({ 
        vaultId: deletedInfo.vaultId,
        forceUpload: true 
      })).unwrap();

      // Transition machine: Registering → Idle with new server info
      if (machine && machine.status === 'registering') {
        machine.registered({
          serverId: result.newSyncId ?? deletedInfo.vaultId,
          serverLastModified: result.lastModified,
          lastSyncDate: new Date().toISOString(),
        });
      }
      
      // Reset sync state
      dispatch(clearVaultDeleted());
      
      // Close dialog
      onOpenChange(false);
    } catch (error) {
      console.error('[Vault Deleted Dialog] Failed to re-upload vault:', error);

      // Recover machine state on failure
      const machine = syncMachineService.get(deletedInfo.vaultId);
      if (machine && machine.status === 'registering') {
        try { machine.fail('upload', (error as Error).message); } catch { /* ignore */ }
        try { machine.recoverToLocalOnly(); } catch { /* ignore */ }
      }

      alert('Failed to re-upload vault. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" onOpenAutoFocus={(e) => e.preventDefault()} onInteractOutside={ e => e.preventDefault() }>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertIcon className="h-5 w-5 text-destructive" />
            <DialogTitle>Vault Deleted on Server</DialogTitle>
          </div>
          <DialogDescription>
            This vault has been deleted on the server, but still exists locally.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <p className="text-sm text-muted-foreground mb-4">
            The vault <strong>{deletedInfo.vaultId}</strong> was deleted on another device or by another user.
            You can either delete it locally to match the server, or re-upload it to restore it on the server.
          </p>
          
          <div className="space-y-3">
            <div className="rounded-lg border p-4">
              <h4 className="font-medium mb-1">Delete Locally</h4>
              <p className="text-sm text-muted-foreground">
                Remove this vault from your local storage to match the server state.
                All local changes will be permanently lost.
              </p>
            </div>
            
            <div className="rounded-lg border p-4">
              <h4 className="font-medium mb-1">Re-upload to Server</h4>
              <p className="text-sm text-muted-foreground">
                Upload your local version back to the server, restoring the vault with your current data.
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2">
          <SecondaryButton
            onClick={() => onOpenChange(false)}
            disabled={processing}
          >
            Cancel
          </SecondaryButton>
          <Button
            variant="destructive"
            onClick={handleDeleteLocal}
            disabled={processing}
          >
            {processing ? 'Deleting...' : 'Delete Locally'}
          </Button>
          <PrimaryButton
            onClick={handleReupload}
            disabled={processing}
          >
            {processing ? 'Re-uploading...' : 'Re-upload to Server'}
          </PrimaryButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
